<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition"
            @click:outside="$emit('close')">
    <v-card>
      <v-app-bar dark color="primary" fixed absolute elevation="4" class="fixed-toolbar">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t(`user.title.${edit ? 'update' : 'create'}`, {name: user.name}) }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn dark text :loading="loading" @click="submitForm">
          {{ $t(`user.button.${edit ? 'update' : 'create'}`) }}
        </v-btn>
      </v-app-bar>

      <v-container fluid class="toolbar-padding">
        <v-row justify="center">
          <v-col cols="12" md="8" lg="6">
            <v-card class="elevation-0 mb-6">
              <v-card-text>
                <v-form ref="userForm" v-model="isFormValid" lazy-validation>
                  <!-- Profile Photo Upload Section -->
                  <div class="profile-upload-section mb-6">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <!-- Current Photo Display -->
                        <div v-if="currentPhoto || preview" class="text-center mb-4">
                          <p class="text-subtitle-2 mb-2">{{ $t('user.label.current_photo') }}</p>
                          <v-avatar size="150" class="mb-3">
                            <v-img :src="preview || currentPhoto" />
                          </v-avatar>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <!-- Upload New Photo -->
                        <div class="upload-section">
                          <p class="text-subtitle-2 mb-2">
                            {{ currentPhoto ? $t('user.label.replace_photo') : $t('user.label.select_photo') }}
                          </p>

                          <v-file-input v-model="photoFile" :label="$t('user.label.select_photo')"
                            prepend-icon="mdi-camera" outlined dense accept="image/png, image/jpeg, image/jpg"
                            :rules="photoRules" show-size @change="onPhotoChange">
                            <template v-slot:selection="{ text }">
                              <v-chip small label color="primary">
                                {{ text }}
                              </v-chip>
                            </template>
                          </v-file-input>

                          <!-- Note: Photo will be uploaded when form is submitted -->
                          <v-alert
                            v-if="photoFile"
                            type="info"
                            text
                            dense
                            border="left"
                            class="mt-3"
                          >
                            {{ $t('user.photo.will_upload_on_submit') }}
                          </v-alert>
                        </div>

                        <!-- Photo Guidelines -->
                        <v-alert
                          type="info"
                          text
                          dense
                          border="left"
                          class="mt-4"
                        >
                          <div class="text-subtitle-2 font-weight-medium mb-2">
                            {{ $t('user.photo.guidelines_title') }}
                          </div>
                          <ul class="text-caption pl-4 mb-0">
                            <li>{{ $t('user.photo.guideline1') }}</li>
                            <li>{{ $t('user.photo.guideline2') }}</li>
                            <li>{{ $t('user.photo.guideline3') }}</li>
                          </ul>
                        </v-alert>
                      </v-col>
                    </v-row>
                  </div>

                  <v-divider class="mb-6"></v-divider>

                  <!-- User Information Form -->
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        ref="nameInput"
                        v-model="form.name"
                        :label="$t('user.label.full_name')"
                        :rules="nameRules"
                        required
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.username"
                        :label="$t('user.label.username_login')"
                        :rules="usernameRules"
                        required
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.email"
                        :label="$t('user.label.email')"
                        :rules="emailRules"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.department_id"
                        :items="departmentItems"
                        :label="$t('user.label.department')"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        clearable
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6" v-if="passwordRequired">
                      <v-text-field
                        v-model="form.password"
                        :label="$t('user.label.password')"
                        :rules="passwordRules"
                        type="password"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" v-if="passwordRequired">
                      <v-text-field
                        v-model="form.password_confirmation"
                        :label="$t('user.label.confirm_password')"
                        :rules="confirmPasswordRules"
                        type="password"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" v-if="passwordRequired">
                      <p class="text-subtitle-2 mb-2">{{ $t('user.label.assign_role') }}</p>
                      <v-chip-group
                        v-model="form.role"
                        column
                      >
                        <v-chip
                          v-for="role in systemRoles"
                          :key="role.id"
                          :value="role.id"
                          filter
                          outlined
                          @click="handleRoleClick(role.id)"
                        >
                          {{ role.name }}
                        </v-chip>
                      </v-chip-group>
                    </v-col>
                    <v-col cols="12">
                      <v-switch
                        v-model="form.active"
                        :label="$t('user.label.activate')"
                        color="success"
                        hide-details
                      ></v-switch>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor" bottom right>
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackbarText }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
export default {
  name: 'CreateEditUser',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    user: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data () {
    return {
      dialog: this.visible,
      departments: [],
      systemRoles: [],
      form: {
        name: this.user.name || '',
        username: this.user.username || null,
        email: this.user.email || '',
        department_id: this.user.department_id || null,
        password: '',
        password_confirmation: '',
        role: null,
        active: (this.user.active !== 0)
      },
      loading: false,
      passwordRequired: true,
      selectedRole: null,
      isFormValid: true,

      // Photo upload related data
      photoFile: null,
      preview: null,
      currentPhoto: null,
      originalPhoto: null, // Store original photo URL when editing
      photoUploading: false,

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      snackbarIcon: 'mdi-check-circle',

      // Validation rules
      nameRules: [
        v => !!v || 'Name is required'
      ],
      usernameRules: [
        v => !!v || 'Username is required',
        v => /^[a-zA-Z0-9-_]+$/.test(v) || 'Username can only contain letters, numbers, hyphens, and underscores'
      ],
      emailRules: [
        v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'E-mail must be valid'
      ],
      passwordRules: [
        v => this.passwordRequired ? !!v || 'Password is required' : true,
        v => this.passwordRequired ? v.length >= 6 || 'Password must be at least 6 characters' : true
      ],
      confirmPasswordRules: [
        v => this.passwordRequired ? !!v || 'Password confirmation is required' : true,
        v => this.passwordRequired ? v === this.form.password || 'Passwords must match' : true
      ],
      photoRules: [
        v => !v || v.size < 2000000 || this.$t('user.photo.size_error')
      ]
    }
  },
  computed: {
    edit () {
      return this.user && this.user.id
    },
    departmentItems () {
      return this.departments.map(dep => ({
        text: dep.name,
        value: dep.id
      }))
    }
  },
  watch: {
    visible (val) {
      this.dialog = val
    },
    dialog (val) {
      if (!val) {
        this.$emit('close')
      }
    },
    'form.active' (value) {
      if (typeof value === 'number') {
        this.form.active = this.form.active !== 0
      }
    },
    user: {
      handler (newVal) {
        if (newVal && newVal.id) {
          this.initializeForm()
        }
      },
      deep: true
    }
  },
  methods: {
    onPhotoChange (file) {
      if (!file) {
        this.preview = null
        return
      }

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.preview = e.target.result
      }
      reader.readAsDataURL(file)
    },

    showSnackbar (text, type = 'success') {
      this.snackbarText = text
      this.snackbarColor = type
      this.snackbarIcon = type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'
      this.snackbar = true
    },

    handleRoleClick (roleId) {
      if (this.form.role === roleId) {
        this.$nextTick(() => {
          this.form.role = null
        })
      }
    },

    resetForm () {
      this.form = {
        name: '',
        username: '',
        email: '',
        department_id: null,
        password: '',
        password_confirmation: '',
        role: null,
        active: true
      }
      this.photoFile = null
      this.preview = null
      this.currentPhoto = null
      this.originalPhoto = null

      if (this.$refs.userForm) {
        this.$refs.userForm.resetValidation()
      }
    },

    async submitForm () {
      if (this.$refs.userForm.validate()) {
        const user = this.user
        const editing = this.edit
        this.askQuestion(this.$t(`user.confirm.${this.edit ? 'update' : 'create'}`), async () => {
          this.loading = true
          try {
            // Determine if we need to use FormData (when photo file is present)
            const hasPhotoFile = this.photoFile && this.photoFile instanceof File
            let submissionData

            if (hasPhotoFile) {
              // Create FormData when photo file is present
              const formData = new FormData()

              // Append all form fields to FormData
              Object.keys(this.form).forEach(key => {
                const value = this.form[key]
                if (value !== null && value !== undefined) {
                  // Handle arrays and objects by stringifying them
                  if (Array.isArray(value) || (typeof value === 'object' && value !== null && !(value instanceof File))) {
                    formData.append(key, JSON.stringify(value))
                  } else {
                    formData.append(key, value)
                  }
                }
              })

              // Append photo file
              formData.append('photo', this.photoFile)
              submissionData = formData
            } else {
              // Use regular object format
              submissionData = { ...this.form }

              // When updating, if photo wasn't changed but original exists, include the URL
              if (editing && !hasPhotoFile && this.originalPhoto) {
                submissionData.photo = this.originalPhoto
              }
            }

            let data
            if (editing) {
              data = await this.$userService.updateUser(user.id, submissionData)
              this.showSuccess(this.$t('user.message.updated'))
              this.$emit('updated', data)
            } else {
              data = await this.$userService.createUser(submissionData)
              this.showSuccess(this.$t('user.message.created'))
              this.$emit('created', data)
            }

            this.$emit('user-ok')
            this.resetForm()
            this.$emit('close', false)
          } catch (error) {
            // Error is already handled by the global interceptor
            console.log('Component caught error in saveUser:', error)
          } finally {
            this.loading = false
          }
        })
      }
    },

    async fetchDepartments () {
      try {
        this.departments = await this.$departmentService.getDepartments()
      } catch (error) {
        console.error('Failed to fetch departments:', error)
      }
    },

    async fetchSystemRoles () {
      try {
        this.systemRoles = [] // await this.$roleService.getSystemRoles()
      } catch (error) {
        console.error('Failed to fetch system roles:', error)
      }
    },

    initializeForm () {
      if (this.user && this.user.id) {
        this.passwordRequired = false
        this.form = {
          name: this.user.name || '',
          username: this.user.username || '',
          email: this.user.email || '',
          department_id: this.user.department_id || null,
          password: '',
          password_confirmation: '',
          role: this.user.role || null,
          active: this.user.active !== 0
        }

        // Set current photo and store original photo URL if available
        if (this.user.photo) {
          this.currentPhoto = this.user.photo
          this.originalPhoto = this.user.photo
        } else {
          this.currentPhoto = null
          this.originalPhoto = null
        }
      } else {
        this.passwordRequired = true
        this.resetForm()
      }

      // Focus on name input after initialization
      this.$nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus()
        }
      })
    }
  },
  mounted () {
    this.fetchDepartments()
    this.fetchSystemRoles()
    this.initializeForm()
  }
}
</script>

<style >
.profile-upload-section {
  border-radius: 8px;
  padding: 16px;
  background-color: #f9f9f9;
}

.theme--dark .profile-upload-section {
  background-color: #1e1e1e;
}

.v-avatar {
  border: 2px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.theme--dark .v-avatar {
  border-color: #424242;
}

.upload-section {
  display: flex;
  flex-direction: column;
}

/* Add padding to account for fixed toolbar */
.v-dialog .toolbar-padding {
  padding-top: 64px; /* Height of the toolbar */
}

/* Fixed toolbar styles */
.fixed-toolbar {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 9999 !important;
  width: 100% !important;
}

/* Ensure proper spacing on mobile */
@media (max-width: 599px) {
  .profile-upload-section {
    padding: 12px;
  }

  .v-avatar {
    margin-bottom: 24px;
  }
}
</style>
